import type * as Stitches from '@stitches/react'

const defaultTokens = {
  fonts: {
    Inter: "'Inter', sans-serif;",
  },
  fontSizes: {
    12: '.75rem',
    14: '.875rem',
    16: '1rem',
    18: '1.125rem',
    20: '1.25rem',
    24: '1.5rem',
    28: '1.75rem',
    36: '2.25rem',
    40: '2.5rem',
    44: '2.75rem',
    48: '3rem',
  },
  lineHeight: {
    18: '1.125rem',
    20: '1.25rem',
    24: '1.5rem',
    28: '1.688rem',
    30: '1.875rem',
    32: '2rem',
    40: '2.5rem',
    44: '2.75rem',
    52: '3.25rem',
    60: '3.75rem',
    66: '4.125rem',
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  space: {
    0: 0,
    1: '0.0625rem',
    2: '.125rem',
    3: '.188rem',
    4: '.25rem',
    5: '.313rem',
    6: '.375rem',
    7: '.438rem',
    8: '.5rem',
    9: '.563rem',
    10: '.625rem',
    12: '.75rem',
    14: '.875rem',
    15: '.938rem',
    16: '1rem',
    18: '1.125rem',
    20: '1.25rem',
    22: '1.375rem',
    24: '1.5rem',
    25: '1.563rem',
    28: '1.75rem',
    30: '1.875rem',
    32: '2rem',
    36: '2.25rem',
    40: '2.5rem',
    44: '2.75rem',
    48: '3rem',
    50: '3.125rem',
    52: '3.25rem',
    56: '3.5rem',
    60: '3.75rem',
    64: '4rem',
  },
  size: {
    2: '.125rem',
    4: '.25rem',
    6: '.375rem',
    7: '.438rem',
    8: '.5rem',
    9: '.563rem',
    10: '.625rem',
    12: '.75rem',
    14: '.875rem',
    16: '1rem',
    18: '1.125rem',
    20: '1.25rem',
    22: '1.375rem',
    24: '1.5rem',
    30: '1.875rem',
    32: '2rem',
    36: '2.25rem',
    40: '2.5rem',
    42: '2.625rem',
    44: '2.75rem',
    48: '3rem',
    50: '3.25rem',
    52: '3.25rem',
    56: '3.5rem',
    64: '4rem',
    72: '4.5rem',
    80: '5rem',
    88: '5.5rem',
    96: '6rem',
  },
  breakpoints: {
    xxs: '280px',
    xs: '375px',
    sm: '600px',
    md: '1024px',
    lg: '1400px',
    xl: '1620px',
  },
  radii: {
    xxs: '.25rem',
    xs: '.375rem',
    sm: '.5rem',
    md: '.75rem',
    base: '1rem',
    lg: '1.5rem',
    xl: '2.5rem',
    squared: '33%',
    rounded: '50%',
    pill: '$sm',
  },
  transition: {
    easeOutQuint: 'cubic-bezier(0.22, 1, 0.36, 1)',
    easeOutBack: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
}

const defaultColors = {
  // generic colors
  whiteBase: '$white100',
  blackBase: '$black100',

  // brand primary colors
  primaryBase: '$primary600',
  primaryBgSubtle: '$primary100',
  primaryLine: '$primaryBase',

  primaryBg: '$primaryBase',
  primaryBgLight: '$primary50',
  primaryBgHover: '$primary700',
  primaryBgActive: '$primaryBase',

  primaryBorder: '$primary200',
  primaryBorderFocus: '$primaryBase',
  primaryBorderHover: '$primary500',

  primaryDisabled: '$primary200',

  primarySolid: '$primaryBase',
  primarySolidHover: '$primary800',
  primaryText: '$primary700',

  // brand secondary colors
  secondaryBase: '$secondary600',

  // brand success colors
  success: '$success700',
  successBorder: '$success200',
  successBg: '$success100',
  successBgHover: '$success400',
  successSolidBg: '$success600',

  // brand warning colors
  warning: '$warning700',
  warningBorder: '$warning200',
  warningBg: '$warning100',
  warningBgHover: '$warning400',
  warningSolidBg: '$warning600',

  // brand error colors
  error: '$error700',
  errorBorder: '$error200',
  errorBg: '$error100',
  errorBgHover: '$error400',
  errorSolidBg: '$error600',

  gradient: 'linear-gradient(45deg, $primary700 0%, $primary600 100%)',
  gradientGreyNeutral: 'linear-gradient(180deg, $gray50 0%, $gray200 100%)',

  // misc input
  inputBg: '$gray50',
  inputText: '$gray900',
  inputLabel: '$gray700',
  inputBorder: '$gray300',
  inputBorderFocus: '$primary300',
  inputBgWarning: '$warningBg',
  inputBorderWarning: '$warning',
  inputBorderDashedWarning: '$warning',
  inputErrorBorder: '$error300',

  checkboxBg: '$primary50',
  checkboxBgHover: '$primary100',
  checkboxCheck: '$primary500',
  checkboxBorder: '$primary500',
  checkboxBorderHover: '$primaryBase',

  bg: '$gradient',
  greyBg: '$gradientGreyNeutral',
  bgBody: '$gray100',
  border: '$gray200',
  borderDark: '$gray400',
  heading: '$gray900',
  text: '$gray600',
  titleBlack: '$blackBase',
  titleWhite: '$whiteBase',
  titleGray: '$gray700',
  selection: '$primary500',
  placeholder: '$gray500',
  defaultIcon: '$gray500',

  disabledBg: '$gray100',
  disabledHoverBg: '$gray200',
  disabledText: '$gray500',
  disabledIcon: '$gray500',
  disabledHintIcon: '$gray400',
  disabledBorder: '$disabledBg',

  modalBg: '$blackLight40',
  notificationBg: '$grayLight7',
  closeBg: '$error100',

  cancelBg: '$errorBg',
  cancelText: '$error',

  dropdownBg: '$inputBg',
  dropdownBorder: '$inputBorder',
  dropdownBorderFocus: '$inputBorderFocus',
  dropdownText: '$inputText',

  tabBg: '$grayLight3',
  tabBgActive: '$blackBase',
  tabActive: '$blue',
  tabActiveColor: '$whiteBase',

  toggleCircle: '$primaryBase',
  toggleBg: '$primaryBg',

  sliderBg: '$border',
  tagGrayBg: '$gray50',

  buttonDisabledBg: '$gray50',
  buttonDisabledText: '$gray400',
  buttonDisabledBorder: '$inputBorder',

  backgroundLinear:
    'linear-gradient(360deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
}

const defaultShadows = {
  inputShadow:
    '0px 0px 0px 4px #ECF8FF, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  dropdownBoxShadow:
    '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
}

const defaultMedia = {
  xss: `(min-width: ${defaultTokens.breakpoints.xxs})`,
  xs: `(min-width: ${defaultTokens.breakpoints.xs})`,
  sm: `(min-width: ${defaultTokens.breakpoints.sm})`,
  md: `(min-width: ${defaultTokens.breakpoints.md})`,
  lg: `(min-width: ${defaultTokens.breakpoints.lg})`,
  xl: `(min-width: ${defaultTokens.breakpoints.xl})`,
  motion: '(prefers-reduced-motion: reduce)',
  dark: '(prefers-color-scheme: dark)',
  light: '(prefers-color-scheme: light)',
}

const defaultUtils = {
  p: (value: Stitches.PropertyValue<'padding'>) => ({
    padding: value,
  }),
  pt: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingTop: value,
  }),
  pr: (value: Stitches.PropertyValue<'paddingRight'>) => ({
    paddingRight: value,
  }),
  pb: (value: Stitches.PropertyValue<'paddingBottom'>) => ({
    paddingBottom: value,
  }),
  pl: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
    paddingLeft: value,
  }),
  px: (value: Stitches.PropertyValue<'paddingLeft'>) => ({
    paddingLeft: value,
    paddingRight: value,
  }),
  py: (value: Stitches.PropertyValue<'paddingTop'>) => ({
    paddingTop: value,
    paddingBottom: value,
  }),
  m: (value: Stitches.PropertyValue<'margin'>) => ({
    margin: value,
  }),
  mt: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginTop: value,
  }),
  mr: (value: Stitches.PropertyValue<'marginRight'>) => ({
    marginRight: value,
  }),
  mb: (value: Stitches.PropertyValue<'marginBottom'>) => ({
    marginBottom: value,
  }),
  ml: (value: Stitches.PropertyValue<'marginLeft'>) => ({
    marginLeft: value,
  }),
  mx: (value: Stitches.PropertyValue<'marginLeft'>) => ({
    marginLeft: value,
    marginRight: value,
  }),
  my: (value: Stitches.PropertyValue<'marginTop'>) => ({
    marginTop: value,
    marginBottom: value,
  }),

  bgColor: (value: Stitches.PropertyValue<'backgroundColor'>) => ({
    backgroundColor: value,
  }),

  size: (value: Stitches.PropertyValue<'width'>) => ({
    width: value,
    height: value,
  }),

  linearGradient: (value: Stitches.PropertyValue<'backgroundImage'>) => ({
    backgroundImage: `linear-gradient(${value})`,
  }),

  transitionButton: (value: Stitches.PropertyValue<'transition'>) => ({
    transition: `color 0.6s ${value},background 0.6s ${value}, border 0.6s ${value}`,
  }),

  textGradient: (value: Stitches.PropertyValue<'backgroundImage'>) => ({
    'backgroundImage': `linear-gradient(${value})`,
    'WebkitBackgroundClip': 'text',
    'WebkitTextFillColor': 'transparent',
    '&::selection': {
      WebkitTextFillColor: '$colors$text',
    },
  }),
}

// const  defaultThemeMap = {};
const commonTheme = {
  theme: {
    ...defaultTokens,
    colors: defaultColors,
    shadows: defaultShadows,
  },
  media: defaultMedia,
  utils: defaultUtils,
  // themeMap: defaultThemeMap,
}

export default commonTheme
